import React, { useState, useEffect, useRef } from 'react';
import { Responsive, WidthProvider } from 'react-grid-layout';
import Chart from 'chart.js/auto';
import { Grid } from 'gridjs';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import 'gridjs/dist/theme/mermaid.css';
import CreateGraphModal from './CreateGraphModal';

const ResponsiveGridLayout = WidthProvider(Responsive);
const LOCAL_STORAGE_KEY_WIDGETS = 'dashboard_widgets';
const LOCAL_STORAGE_KEY_LAYOUT = 'dashboard_layout';

const ChartView = ({ config }) => {
    const canvasRef = useRef(null);
    const chartInstanceRef = useRef(null);

    useEffect(() => {
        if (canvasRef.current) {
            if (chartInstanceRef.current) chartInstanceRef.current.destroy();
            const ctx = canvasRef.current.getContext('2d');
            chartInstanceRef.current = new Chart(ctx, config);
        }
        return () => { if (chartInstanceRef.current) chartInstanceRef.current.destroy(); };
    }, [config]);

    return <canvas ref={canvasRef} style={{ width: '100%', height: '100%' }}></canvas>;
};

const TableView = ({ config }) => {
    const wrapperRef = useRef(null);
    const gridInstanceRef = useRef(null);

    useEffect(() => {
        if (wrapperRef.current && config.data) {
            if (gridInstanceRef.current) gridInstanceRef.current.destroy();
            wrapperRef.current.innerHTML = '';
            const data = (config.data.labels || []).map((label, index) => [
                label,
                ...config.data.datasets.map(dataset => dataset.data[index] ?? null),
            ]);
            const grid = new Grid({
                columns: ['Model', ...config.data.datasets.map(dataset => dataset.label || 'Value')],
                data: data,
                search: true,
                sort: true,
                className: {
                    table: 'w-full text-sm text-left',
                    th: 'bg-gray-100 font-bold text-gray-700',
                    td: 'border-b border-gray-100 p-2'
                }
            });
            grid.render(wrapperRef.current);
            gridInstanceRef.current = grid;
        }
    }, [config]);

    return <div ref={wrapperRef} style={{ maxHeight: '400px', overflowY: 'auto' }} />;
};

const Widget = ({ id, title, chartConfig, onRemove }) => {
    const [view, setView] = useState('chart');

    return (
        <div className="bg-white h-full w-full rounded-lg shadow-card border border-gray-200 flex flex-col overflow-hidden">
            <div className="bg-gray-50 px-3 py-2 border-b border-gray-200 cursor-move flex justify-between items-center flex-shrink-0">
                <h3 className="font-bold text-gray-700 text-xs uppercase tracking-wide truncate ml-1">{title}</h3>
                <div className="flex items-center space-x-2">
                    <select
                        value={view}
                        onChange={(e) => setView(e.target.value)}
                        className="bg-white border border-gray-300 text-gray-700 text-xs rounded focus:ring-primary focus:border-primary block p-1 h-6"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <option value="chart">Chart</option>
                        <option value="table">Table</option>
                    </select>
                    <button onClick={() => onRemove(id)} className="text-gray-400 hover:text-red-500 transition-colors">
                        <span className="text-lg font-bold leading-none">&times;</span>
                    </button>
                </div>
            </div>
            <div className="flex-grow p-4 overflow-hidden relative">
                {view === 'chart' ? <ChartView config={chartConfig} /> : <TableView config={chartConfig} />}
            </div>
        </div>
    );
};

const GraphDashboardComponent = () => {
    const [widgets, setWidgets] = useState([]);
    const [layout, setLayout] = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [loaded, setLoaded] = useState(false);

    useEffect(() => {
        const savedWidgets = localStorage.getItem(LOCAL_STORAGE_KEY_WIDGETS);
        const savedLayout = localStorage.getItem(LOCAL_STORAGE_KEY_LAYOUT);
        if (savedWidgets) setWidgets(JSON.parse(savedWidgets));
        if (savedLayout) setLayout(JSON.parse(savedLayout));
        setLoaded(true);
    }, []);

    useEffect(() => {
        if (loaded) localStorage.setItem(LOCAL_STORAGE_KEY_WIDGETS, JSON.stringify(widgets));
    }, [widgets, loaded]);

    useEffect(() => {
        if (loaded) localStorage.setItem(LOCAL_STORAGE_KEY_LAYOUT, JSON.stringify(layout));
    }, [layout, loaded]);

    const handleRemoveWidget = (widgetId) => {
        setWidgets(widgets.filter(w => w.id !== widgetId));
        setLayout(layout.filter(l => l.i !== widgetId));
    };

    return (
        <div className="min-h-screen">
            <CreateGraphModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                widgets={widgets}
                setWidgets={setWidgets}
                layout={layout}
                setLayout={setLayout}
            />
            <div className="bg-white p-6 rounded-lg shadow-card border border-gray-200 mb-6 flex justify-between items-center">
                <div>
                    <h1 className="text-xl font-bold text-gray-800 uppercase tracking-wide">Custom Graph Dashboard</h1>
                    <p className="text-sm text-gray-500 mt-1">Create and arrange your own analytics widgets.</p>
                </div>
                <button
                    onClick={() => setIsModalOpen(true)}
                    className="btn btn-primary"
                >
                    + Create Graph
                </button>
            </div>
            <ResponsiveGridLayout
                className="layout"
                layouts={{ lg: layout }}
                breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
                cols={{ lg: 12, md: 10, sm: 6, xs: 4, xxs: 2 }}
                rowHeight={100}
                draggableHandle=".widget-header"
                onLayoutChange={(newLayout) => setLayout(newLayout)}
            >
                {widgets.map(widget => (
                    <div key={widget.id}>
                        <Widget
                            id={widget.id}
                            title={widget.title}
                            chartConfig={widget.config}
                            onRemove={handleRemoveWidget}
                        />
                    </div>
                ))}
            </ResponsiveGridLayout>
        </div>
    );
};

export default GraphDashboardComponent;